// src/components/BackendIndicator.js
import React, { useState, useEffect } from 'react';
import { FaServer, FaCheckCircle, FaExclamationTriangle, FaSync } from 'react-icons/fa';
import Seo from './Common/Seo';

const BackendIndicator = () => {
  const [backendInfo, setBackendInfo] = useState(null);
  const [isChecking, setIsChecking] = useState(false);

  const checkBackend = async () => {
    setIsChecking(true);
    
    // Try different possible backend URLs
    const possibleBackends = [
      { name: 'Local', url: 'http://localhost:8000/health' },
      { name: 'Render', url: 'https://whe2-backend.onrender.com/health' }
    ];
    
    for (const backend of possibleBackends) {
      try {
        const startTime = Date.now();
        const response = await fetch(backend.url, {
          method: 'GET',
          mode: 'cors',
          cache: 'no-cache'
        });
        const responseTime = Date.now() - startTime;
        
        if (response.ok) {
          const data = await response.json();
          setBackendInfo({
            name: backend.name,
            url: backend.url.replace('/health', ''),
            status: 'connected',
            responseTime,
            data,
            timestamp: new Date().toISOString()
          });
          return; // Stop checking when we find a working backend
        }
      } catch (error) {
        console.log(`${backend.name} backend not reachable:`, error.message);
      }
    }
    
    // If no backends responded
    setBackendInfo({
      name: 'None',
      url: 'Not reachable',
      status: 'disconnected',
      responseTime: 0,
      data: null,
      timestamp: new Date().toISOString()
    });
    
    setIsChecking(false);
  };

  useEffect(() => {
    checkBackend();
    // Check every 30 seconds
    const interval = setInterval(checkBackend, 30000);
    return () => clearInterval(interval);
  }, []);

  if (!backendInfo) {
    return (
      <div style={{
        position: 'fixed',
        top: '10px',
        right: '10px',
        padding: '8px 12px',
        background: '#fff3cd',
        border: '1px solid #ffc107',
        borderRadius: '6px',
        fontSize: '12px',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        gap: '6px'
      }}>
        <FaSync className="spinner" />
        Checking backend...
      </div>
    );
  }

  const isLocal = backendInfo.url.includes('localhost');
  const isConnected = backendInfo.status === 'connected';

  return (
    <>
      <Seo
        title="Backend Status | Whe2"
        description="Internal backend status panel for Whe2."
        path="/backend-check"
        robots="noindex,nofollow"
      />
      <div
        onClick={checkBackend}
        style={{
          position: 'fixed',
          top: '10px',
          right: '10px',
          padding: '8px 12px',
          background: isConnected
            ? (isLocal ? '#d1ecf1' : '#d4edda')
            : '#f8d7da',
          border: `1px solid ${
            isConnected
              ? (isLocal ? '#bee5eb' : '#c3e6cb')
              : '#f5c6cb'
          }`,
          borderRadius: '6px',
          fontSize: '12px',
          cursor: 'pointer',
          zIndex: 1000,
          maxWidth: '250px',
          transition: 'all 0.3s ease'
        }}
        title={`Click to check again\nLast checked: ${new Date(backendInfo.timestamp).toLocaleTimeString()}`}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <FaServer size={12} color={isLocal ? '#0dcaf0' : (isConnected ? '#28a745' : '#dc3545')} />
          <span style={{ fontWeight: '500' }}>
            {isLocal ? 'Local' : 'Render'} Backend
          </span>
          {isChecking ? (
            <FaSync className="spinner" size={10} />
          ) : isConnected ? (
            <FaCheckCircle size={10} color="#28a745" />
          ) : (
            <FaExclamationTriangle size={10} color="#dc3545" />
          )}
        </div>

        <div style={{
          marginTop: '4px',
          fontSize: '11px',
          color: '#666',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span style={{
            background: isLocal ? '#0dcaf0' : '#6c757d',
            color: 'white',
            padding: '2px 6px',
            borderRadius: '10px',
            fontSize: '10px'
          }}>
            {isLocal ? 'localhost:8000' : 'whe2-backend.onrender.com'}
          </span>
          <span>
            {isConnected ? `${backendInfo.responseTime}ms` : 'Offline'}
          </span>
        </div>
      </div>
    </>
  );
};

export default BackendIndicator;
